import { useState, useEffect, useRef } from "react"
import { Helmet } from "react-helmet-async"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Navigation } from "@/components/Navigation"
import { useAuth } from "@/contexts/AuthContext"
import { ToastContainer } from "@/components/ui/toast"
import { useToast } from "@/hooks/use-toast"
import { Loader2, CreditCard, Calendar, Check, Zap, X, Clock } from "lucide-react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { addCSRFHeader } from "@/utils/csrf"
import { fetchWithRefresh } from "@/utils/fetchWithRefresh"

interface SubscriptionStatus {
  subscription_tier: string
  has_used_trial: boolean
  trial_days_remaining: number
  scans_per_day: number
  scans_remaining_today: number
  has_stripe_subscription: boolean
  stripe_status?: string
  current_period_end?: number
  cancel_at_period_end?: boolean
}

export default function Billing() {
  const { checkAuthStatus } = useAuth()
  const { toasts, removeToast, toast } = useToast()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<SubscriptionStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const hasShownToast = useRef(false)

  // Check for checkout success/cancel from URL params
  useEffect(() => {
    if (hasShownToast.current) return

    const success = searchParams.get('success')
    const canceled = searchParams.get('canceled')

    if (success === 'true') {
      hasShownToast.current = true
      toast({
        title: "Subscription successful!",
        description: "Your Pro subscription is now active. Enjoy 50 scans per day!",
      })
      // Clear URL params
      navigate('/billing', { replace: true })
      // Refresh auth to get updated tier
      checkAuthStatus()
    } else if (canceled === 'true') {
      hasShownToast.current = true
      toast({
        title: "Checkout canceled",
        description: "You can upgrade to Pro anytime.",
      })
      // Clear URL params
      navigate('/billing', { replace: true })
    }
  }, [searchParams, toast, navigate, checkAuthStatus])

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true)
      const response = await fetchWithRefresh('/api/billing/status', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (response.ok) {
        const data = await response.json()
        setStatus(data)
      } else {
        toast({
          title: "Error",
          description: "Failed to load subscription status",
          variant: "destructive"
        })
      }
    } catch (err) {
      console.error('Failed to fetch subscription status:', err)
      toast({
        title: "Error",
        description: "Failed to connect to server",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSubscriptionStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleUpgradeToPro = async () => {
    try {
      setActionLoading(true)
      const response = await fetchWithRefresh('/api/billing/create-checkout-session', {
        method: 'POST',
        credentials: 'include',
        headers: addCSRFHeader({
          'Content-Type': 'application/json',
        })
      })

      if (response.ok) {
        const data = await response.json()
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url
      } else {
        const errorData = await response.json()
        toast({
          title: "Error",
          description: errorData.error || "Failed to start checkout",
          variant: "destructive"
        })
      }
    } catch (err) {
      console.error('Failed to create checkout session:', err)
      toast({
        title: "Error",
        description: "Failed to connect to payment system",
        variant: "destructive"
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleManageBilling = async () => {
    try {
      setActionLoading(true)
      const response = await fetchWithRefresh('/api/billing/create-portal-session', {
        method: 'POST',
        credentials: 'include',
        headers: addCSRFHeader({
          'Content-Type': 'application/json',
        })
      })

      if (response.ok) {
        const data = await response.json()
        // Redirect to Stripe Customer Portal
        window.location.href = data.portal_url
      } else {
        const errorData = await response.json()
        toast({
          title: "Error",
          description: errorData.error || "Failed to open billing portal",
          variant: "destructive"
        })
      }
    } catch (err) {
      console.error('Failed to create portal session:', err)
      toast({
        title: "Error",
        description: "Failed to connect to billing system",
        variant: "destructive"
      })
    } finally {
      setActionLoading(false)
    }
  }

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="flex min-h-screen w-full flex-col">
        <Navigation />
        <main className="flex flex-1 flex-col gap-4 px-4 pb-4 md:gap-8 md:px-8 md:pb-8 pt-20">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen w-full flex-col">
      <Helmet>
        <title>Billing | Fusegu Security</title>
        <meta name="description" content="Manage your subscription and billing settings." />
      </Helmet>
      <Navigation />
      <ToastContainer toasts={toasts} onDismiss={removeToast} />

      <main className="flex flex-1 flex-col gap-4 px-4 pb-4 md:gap-8 md:px-8 md:pb-8 pt-20">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Billing & Subscription</h1>
            <p className="text-muted-foreground">
              Manage your subscription and billing settings
            </p>
          </div>
        </div>

        {/* Plan Comparison */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Current Plan */}
          <Card className={status?.subscription_tier === 'pro' ? 'border-2 border-blue-500' : ''}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  {status?.subscription_tier === 'none' ? 'Free' : status?.subscription_tier === 'trial' ? 'Trial' : 'Pro'}
                </CardTitle>
                <Badge className={status?.subscription_tier === 'pro' ? 'bg-blue-600' : status?.subscription_tier === 'trial' ? 'bg-blue-500' : 'bg-gray-500'}>
                  Current Plan
                </Badge>
              </div>
              <CardDescription>
                {status?.subscription_tier === 'none' && 'Basic access to get started'}
                {status?.subscription_tier === 'trial' && 'Try all features for 7 days'}
                {status?.subscription_tier === 'pro' && 'Full access to all features'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Price */}
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold">
                  {status?.subscription_tier === 'pro' ? '$49' : '$0'}
                </span>
                <span className="text-muted-foreground">/month</span>
              </div>

              {/* Features */}
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  {(status?.scans_per_day || 0) > 0 ? (
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <X className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium">{status?.scans_per_day || 0} scans per day</p>
                    {status?.scans_remaining_today !== undefined && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {status.scans_remaining_today} scans remaining today
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  {status?.subscription_tier === 'pro' || status?.subscription_tier === 'trial' ? (
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <X className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  )}
                  <span>All 11 security scanning tools</span>
                </div>

                <div className="flex items-start gap-3">
                  {status?.subscription_tier === 'pro' || status?.subscription_tier === 'trial' ? (
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <X className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  )}
                  <span>Scan any website</span>
                </div>

                <div className="flex items-start gap-3">
                  {status?.subscription_tier === 'pro' ? (
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  ) : status?.subscription_tier === 'trial' ? (
                    <Clock className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <X className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    {status?.subscription_tier === 'trial' ? (
                      <span>{status.trial_days_remaining} days remaining</span>
                    ) : (
                      <span>No time restrictions</span>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  {status?.subscription_tier === 'pro' || status?.subscription_tier === 'trial' ? (
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <X className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  )}
                  <span>Comprehensive security reports</span>
                </div>
              </div>

              {/* Billing Info */}
              {status?.subscription_tier === 'pro' && status?.current_period_end && (
                <div className="pt-4 border-t">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className={status.cancel_at_period_end ? 'text-destructive' : 'text-muted-foreground'}>
                      {status.cancel_at_period_end ? 'Ends' : 'Renews'} {formatDate(status.current_period_end)}
                    </span>
                  </div>
                  {status.cancel_at_period_end && (
                    <p className="text-xs text-destructive mt-2">
                      Subscription will not renew
                    </p>
                  )}
                </div>
              )}

              {/* Action Button */}
              <div className="pt-4">
                {status?.subscription_tier === 'pro' && status?.has_stripe_subscription ? (
                  <Button
                    onClick={handleManageBilling}
                    disabled={actionLoading}
                    variant="outline"
                    className="w-full"
                  >
                    {actionLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <CreditCard className="mr-2 h-4 w-4" />
                        Manage Billing
                      </>
                    )}
                  </Button>
                ) : null}
              </div>
            </CardContent>
          </Card>

          {/* Pro Plan (if not on Pro) */}
          {status?.subscription_tier !== 'pro' && (
            <Card className="border-2 border-blue-500 shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-blue-600" />
                    Pro
                  </CardTitle>
                  <Badge className="bg-blue-600">Recommended</Badge>
                </div>
                <CardDescription>
                  Full access to all security scanning features
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Price */}
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-blue-600">$49</span>
                  <span className="text-muted-foreground">/month</span>
                </div>

                {/* Features */}
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="font-medium">50 scans per day</span>
                  </div>

                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>All 11 security scanning tools</span>
                  </div>

                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Scan any website</span>
                  </div>

                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>No time restrictions</span>
                  </div>

                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Comprehensive security reports</span>
                  </div>
                </div>

                {/* Action Button */}
                <Button
                  onClick={handleUpgradeToPro}
                  disabled={actionLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
                  size="lg"
                >
                  {actionLoading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Zap className="mr-2 h-5 w-5" />
                      Upgrade to Pro
                    </>
                  )}
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  Cancel anytime. No contracts or commitments.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Show "You're on Pro" message if on Pro */}
          {status?.subscription_tier === 'pro' && (
            <Card className="border-2 border-blue-200 bg-blue-50 dark:bg-blue-950">
              <CardContent className="flex flex-col items-center justify-center h-full py-12">
                <Check className="h-16 w-16 text-blue-600 mb-4" />
                <h3 className="text-2xl font-bold text-blue-900 dark:text-blue-100 mb-2">
                  You're on the Pro plan!
                </h3>
                <p className="text-blue-700 dark:text-blue-300 text-center mb-6">
                  Enjoying full access to all security scanning features
                </p>
                <div className="text-sm text-blue-600 dark:text-blue-400 space-y-1">
                  <p>✓ 50 scans per day</p>
                  <p>✓ All security tools</p>
                  <p>✓ No time restrictions</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  )
}
